import { useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { Users, ClipboardList, ArrowRight, Loader2 } from "lucide-react";
import { cn } from "@/lib/cn";
import { displayCustomerName } from "@/lib/format";
import type { CustomerWithLocations } from "@/features/customer-360/api";
import { CustomerAvatar } from "@/features/customer-360/CustomerAvatar";
import {
  useUniversalSearch,
  type UniversalSearchState,
} from "./useUniversalSearch";
import type { DispatchSearchResult } from "./useDispatchSearch";

// ============================================================================
// UniversalSearchDropdown — header-anchored live-search dropdown.
//
// Renders categorized result rows (Customers, Dispatches) with keyboard
// navigation. Each row drill-direct-navigates to its detail view on click
// or Enter. Esc closes; outside-click is handled by the parent (Header).
//
// v1 keyboard model:
//   ↑/↓   move selection (across categories, no wrap)
//   Enter open the selected row
//   Esc   close dropdown (handled by parent — input keydown)
//
// v1 scope: Customers + Dispatches. Techs / Properties / etc. land in v1.5+.
// ============================================================================

interface Props {
  query: string;
  /** -1 means "no row selected yet" — Enter falls back to /customers?q=X. */
  selectedIndex: number;
  /** Called when the row count changes — parent clamps its selectedIndex. */
  onRowCountChange: (newCount: number) => void;
  /** Called when user hovers a row — parent sets selectedIndex to it. */
  onHoverIndex: (index: number) => void;
  onClose: () => void;
}

interface FlatRow {
  kind: "customer" | "dispatch";
  data: CustomerWithLocations | DispatchSearchResult;
  href: string;
}

export function UniversalSearchDropdown({
  query,
  selectedIndex,
  onRowCountChange,
  onHoverIndex,
  onClose,
}: Props) {
  const navigate = useNavigate();
  const state = useUniversalSearch(query);

  // Flatten all visible rows into one array for keyboard-navigable indexing.
  const flatRows = useMemo<FlatRow[]>(() => {
    const rows: FlatRow[] = [];
    for (const c of state.customers.rows) {
      const params = new URLSearchParams({ from: "search", q: query });
      rows.push({
        kind: "customer",
        data: c,
        href: `/customers/${c.custno}?${params.toString()}`,
      });
    }
    for (const d of state.dispatches.rows) {
      // BUILD-15 shipped 2026-05-27 — route direct to /dispatch/:id. The
      // detail view's smart-back uses ?from=search&q=... to return cleanly.
      const params = new URLSearchParams({ from: "search", q: query });
      rows.push({
        kind: "dispatch",
        data: d,
        href: `/dispatch/${d.dispatch}?${params.toString()}`,
      });
    }
    return rows;
  }, [state.customers.rows, state.dispatches.rows, query]);

  // Tell parent how many rows we have so its keyboard handler can clamp.
  useEffect(() => {
    onRowCountChange(flatRows.length);
  }, [flatRows.length, onRowCountChange]);

  // Listen for the parent's "open selected" signal via a window event,
  // so the parent's keydown handler can stay simple.
  useEffect(() => {
    function onOpen() {
      const target = flatRows[selectedIndex];
      if (target) {
        navigate(target.href);
        onClose();
      }
    }
    window.addEventListener("pegasus-search-open-selected", onOpen);
    return () =>
      window.removeEventListener("pegasus-search-open-selected", onOpen);
  }, [flatRows, selectedIndex, navigate, onClose]);

  if (state.isLoading && flatRows.length === 0) {
    return (
      <DropdownShell>
        <div className="flex items-center gap-2 px-4 py-6 text-xs text-slate-500">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Searching for &ldquo;{query}&rdquo;…
        </div>
      </DropdownShell>
    );
  }

  if (!state.hasAnyResults) {
    return (
      <DropdownShell>
        <div className="px-4 py-6 text-center text-xs text-slate-500">
          No matches for &ldquo;{query}&rdquo;.
        </div>
      </DropdownShell>
    );
  }

  let runningIndex = 0;
  return (
    <DropdownShell>
      {state.customers.rows.length > 0 && (
        <Section
          icon={Users}
          label="Customers"
          totalAvailable={state.customers.totalAvailable}
          footerHref={`/customers?q=${encodeURIComponent(query)}`}
          footerLabel="View all matching customers in search"
        >
          {state.customers.rows.map((c) => {
            const i = runningIndex++;
            return (
              <CustomerRow
                key={c.custno}
                row={c}
                selected={i === selectedIndex}
                onMouseEnter={() => onHoverIndex(i)}
                onClick={() => {
                  const target = flatRows[i];
                  if (target) {
                    navigate(target.href);
                    onClose();
                  }
                }}
              />
            );
          })}
        </Section>
      )}

      {state.dispatches.rows.length > 0 && (
        <Section
          icon={ClipboardList}
          label="Dispatches"
          totalAvailable={state.dispatches.totalAvailable}
        >
          {state.dispatches.rows.map((d) => {
            const i = runningIndex++;
            return (
              <DispatchRow
                key={d.dispatch}
                row={d}
                selected={i === selectedIndex}
                onMouseEnter={() => onHoverIndex(i)}
                onClick={() => {
                  const target = flatRows[i];
                  if (target) {
                    navigate(target.href);
                    onClose();
                  }
                }}
              />
            );
          })}
        </Section>
      )}
    </DropdownShell>
  );
}

// ----------------------------------------------------------------------------
// Layout primitives
// ----------------------------------------------------------------------------

function DropdownShell({ children }: { children: React.ReactNode }) {
  return (
    <div data-glass="panel" className="absolute right-0 top-full z-50 mt-2 w-[520px] overflow-hidden rounded-2xl">
      <div className="max-h-[480px] overflow-y-auto">{children}</div>
    </div>
  );
}

function Section({
  icon: Icon,
  label,
  totalAvailable,
  footerHref,
  footerLabel,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  totalAvailable: number;
  footerHref?: string;
  footerLabel?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="border-b border-slate-100 last:border-b-0">
      <div className="flex items-center justify-between px-4 pt-3 pb-1.5">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          <Icon className="h-3 w-3" />
          {label}
        </div>
        {totalAvailable > 0 && (
          <span className="text-[11px] text-slate-500">
            {totalAvailable} {totalAvailable === 1 ? "match" : "matches"}
          </span>
        )}
      </div>
      <div>{children}</div>
      {footerHref && footerLabel && (
        <a
          href={footerHref}
          className="flex items-center justify-between border-t border-slate-100 px-4 py-2 text-xs text-slate-500 hover:bg-slate-50 hover:text-brand-700"
        >
          <span>{footerLabel}</span>
          <ArrowRight className="h-3 w-3" />
        </a>
      )}
    </section>
  );
}

// ----------------------------------------------------------------------------
// Row renderers
// ----------------------------------------------------------------------------

function CustomerRow({
  row,
  selected,
  onMouseEnter,
  onClick,
}: {
  row: CustomerWithLocations;
  selected: boolean;
  onMouseEnter: () => void;
  onClick: () => void;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (selected) ref.current?.scrollIntoView({ block: "nearest" });
  }, [selected]);

  const name = displayCustomerName(null, row.fullname);
  const primaryLoc =
    row.locations?.find((l) => !l.location_inactive) ?? row.locations?.[0];
  const subtitleParts: string[] = [];
  if (primaryLoc?.add1) subtitleParts.push(primaryLoc.add1);
  if (primaryLoc?.city) subtitleParts.push(primaryLoc.city);
  const subtitle = subtitleParts.join(", ");

  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      className={cn(
        "flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors",
        selected ? "bg-brand-50" : "hover:bg-slate-50",
      )}
    >
      <CustomerAvatar custno={row.custno} name={name} size="sm" />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="truncate text-sm font-medium text-slate-900">
            {name}
          </span>
          {row.customerinactive && (
            <span className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
              Inactive
            </span>
          )}
        </div>
        {subtitle && (
          <div className="truncate text-xs text-slate-500">{subtitle}</div>
        )}
      </div>
      <span className="flex-shrink-0 font-mono text-xs text-slate-500">
        {row.custno}
      </span>
    </button>
  );
}

function DispatchRow({
  row,
  selected,
  onMouseEnter,
  onClick,
}: {
  row: DispatchSearchResult;
  selected: boolean;
  onMouseEnter: () => void;
  onClick: () => void;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (selected) ref.current?.scrollIntoView({ block: "nearest" });
  }, [selected]);

  let dateLabel = "—";
  try {
    dateLabel = format(parseISO(row.appointment_date), "MMM d, yyyy");
  } catch {
    dateLabel = row.appointment_date || "—";
  }

  const customer =
    displayCustomerName(row.locname, row.customer_fullname) || "—";
  const addressBits: string[] = [];
  if (row.service_address) addressBits.push(row.service_address);
  if (row.city) addressBits.push(row.city);
  const subtitleParts: string[] = [];
  if (row.tech_name) subtitleParts.push(row.tech_name);
  if (addressBits.length > 0) subtitleParts.push(addressBits.join(", "));

  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      className={cn(
        "flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors",
        selected ? "bg-brand-50" : "hover:bg-slate-50",
      )}
    >
      <div className="flex w-16 flex-shrink-0 flex-col items-start">
        <span className="text-xs font-medium text-slate-700">{dateLabel}</span>
        {row.window_code && (
          <span className="mt-0.5 rounded-full bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-600">
            {row.window_code}
          </span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-slate-900">
          {customer}
        </div>
        {subtitleParts.length > 0 && (
          <div className="truncate text-xs text-slate-500">
            {subtitleParts.join(" · ")}
          </div>
        )}
      </div>
      <span className="flex-shrink-0 font-mono text-xs text-slate-500">
        #{row.dispatch}
      </span>
    </button>
  );
}

// Exported so the parent can satisfy unused-import linters if needed.
export type { UniversalSearchState };
